import React from 'react'
import styled from 'styled-components'
import { colors, starOrbs } from '@src/theme/sharedComponents'
import designConfig from '@design-config'
import FeaturedProductStrip from './FeaturedProductStrip'
import ManufacturersSlider from './ManufacturersSlider'
import { usePublicSettings } from '@src/hooks/queries/useSettings'
import { usePublicFeaturedProducts } from '@src/hooks/queries/useFeaturedProducts'
import { useCategoryImageMap } from '@src/hooks/useCategoryImageMap'
import type { FeaturedProduct } from '@src/types'

const HeroSection = styled.section<{ $stars?: boolean }>`
  background: ${colors.primaryDark};
  border-bottom: 2px solid ${colors.primary};
  position: relative;
  overflow: hidden;
  ${p => p.$stars && starOrbs}
`

const HeroRow = styled.div<{ $centered?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0;
  max-width: 1600px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  justify-content: ${p => p.$centered ? 'center' : 'flex-start'};
  text-align: ${p => p.$centered ? 'center' : 'left'};

  @media (max-width: 900px) {
    flex-direction: column;
    text-align: center;
  }
`

const TextBlock = styled.div`
  flex: 0 0 auto;
  padding: 1.5rem 2rem;
  border-right: 1px solid rgba(255,255,255,0.08);

  @media (max-width: 900px) {
    border-right: none;
    border-bottom: 1px solid rgba(255,255,255,0.08);
    padding: 1.25rem 1.5rem;
    width: 100%;
  }
`

const Eyebrow = styled.div`
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: ${colors.accent};
  margin-bottom: 0.3rem;
`

const Title = styled.h1`
  margin: 0 0 0.25rem;
  font-size: clamp(1.4rem, 3vw, 2rem);
  font-weight: 900;
  color: white;
  line-height: 1;
  font-family: 'Barlow Condensed', 'Impact', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.02em;
`

const Subtitle = styled.p`
  margin: 0;
  color: rgba(255,255,255,0.6);
  font-size: 0.85rem;
`

const CarouselContainer = styled.div`
  flex: 1;
  overflow: hidden;

  @media (max-width: 900px) { display: none; }
`

interface ProductsPageHeaderProps {
  onProductClick?: (fp: FeaturedProduct) => void
}

const ProductsPageHeader: React.FC<ProductsPageHeaderProps> = ({ onProductClick }) => {
  const { data: settingsData } = usePublicSettings()
  const { data: featuredResponse } = usePublicFeaturedProducts()
  const categoryImageMap = useCategoryImageMap()
  const s = settingsData

  const allProducts = featuredResponse?.products || []
  const showCarousel = s?.products_featured_products_carousel_enabled !== false
  const products = showCarousel ? allProducts.slice(0, 10) : []
  const showSlider = s?.products_manufacturers_slider_enabled !== false

  return (
    <>
      <HeroSection $stars={designConfig.themeDecorations[s?.theme_id ?? ''] === 'stars'}>
        <HeroRow $centered={products.length === 0}>
          <TextBlock>
            {s?.products_page_header_eyebrow && <Eyebrow>{s.products_page_header_eyebrow}</Eyebrow>}
            <Title>{s?.products_page_header_title || 'Product Catalog'}</Title>
            <Subtitle>{s?.products_page_header_subtitle || 'Browse our complete product catalog'}</Subtitle>
          </TextBlock>
          {products.length > 0 && (
            <CarouselContainer>
              <FeaturedProductStrip
                products={products}
                categoryImageMap={categoryImageMap}
                onProductClick={fp => onProductClick?.(fp)}
              />
            </CarouselContainer>
          )}
        </HeroRow>
      </HeroSection>
      {showSlider && <ManufacturersSlider compact />}
    </>
  )
}

export default ProductsPageHeader
